import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const planSummary = (p) => `${p.card_limit ? `${p.card_limit.toLocaleString()} cards` : "Unlimited cards"} · ${p.duration_months} month${p.duration_months > 1 ? "s" : ""}`;

const L = ({ label, children }) => <label className="block space-y-1"><span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</span>{children}</label>;

const EMPTY = { name: "", card_limit: "", duration_months: 1, price: "", color: "#F59E0B" };

function PlanForm({ plan, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (plan) setF({ ...EMPTY, ...plan, card_limit: plan.card_limit || "" }); }, [plan]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, price: f.price, color: f.color, duration_months: Number(f.duration_months), card_limit: f.card_limit ? Number(f.card_limit) : null };
      const { data } = plan.id ? await api.put(`/admin/plans/${plan.id}`, body) : await api.post("/admin/plans", body);
      onSaved(data);
      toast.success(`Plan ${data.name} saved`);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-800" data-testid="plan-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{plan?.id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <L label="Name"><input required className="cf-input h-9 text-sm" value={f.name} onChange={set("name")} data-testid="plan-form-name" /></L>
            <L label="Colour"><input type="color" className="h-9 w-14 rounded border border-slate-700 bg-transparent" value={f.color} onChange={set("color")} data-testid="plan-form-color" /></L>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <L label="Card limit"><input type="number" min={1} className="cf-input h-9 text-sm" placeholder="Unlimited" value={f.card_limit} onChange={set("card_limit")} data-testid="plan-form-limit" /></L>
            <L label="Months"><input type="number" min={1} max={120} required className="cf-input h-9 text-sm" value={f.duration_months} onChange={set("duration_months")} data-testid="plan-form-months" /></L>
            <L label="Price"><input className="cf-input h-9 text-sm" placeholder="₹999" value={f.price} onChange={set("price")} data-testid="plan-form-price" /></L>
          </div>
          <button disabled={busy} className="cf-btn-primary w-full h-10" data-testid="plan-form-save">{busy && <Loader2 size={14} className="animate-spin" />} Save package</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PlansPanel({ plans, setPlans }) {
  const [editing, setEditing] = useState(null);
  const remove = async (p) => {
    if (!window.confirm(`Delete package "${p.name}"? Stores keep their current limits and expiry.`)) return;
    try { await api.delete(`/admin/plans/${p.id}`); setPlans((l) => l.filter((x) => x.id !== p.id)); toast.success("Package deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  const saved = (d) => setPlans((l) => (l.some((x) => x.id === d.id) ? l.map((x) => (x.id === d.id ? { ...x, ...d } : x)) : [...l, d]));

  return (
    <section className="space-y-4" data-testid="plans-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-heading font-bold flex items-center gap-2"><Package size={16} className="text-amber-400" /> Plan packages</p>
          <p className="text-xs text-slate-500 mt-1">Applying a package sets the store's card limit and extends its expiry by the package duration.</p>
        </div>
        <button className="cf-btn-primary h-9 text-xs px-3" onClick={() => setEditing({})} data-testid="plan-add-button"><Plus size={13} /> New package</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p, i) => (
          <div key={p.id} className="relative rounded-xl border border-slate-800 bg-slate-900/60 p-5 overflow-hidden" data-testid={`plan-card-${i}`}>
            <div className="absolute inset-x-0 top-0 h-1" style={{ background: p.color }} />
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading text-xl font-extrabold" style={{ color: p.color }} data-testid={`plan-name-${i}`}>{p.name}</p>
                <p className="text-2xl font-bold mt-1">{p.price || "—"}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white" data-testid={`plan-edit-${i}`}><Pencil size={13} /></button>
                <button onClick={() => remove(p)} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" data-testid={`plan-delete-${i}`}><Trash2 size={13} /></button>
              </div>
            </div>
            <p className="text-sm text-slate-300 mt-3" data-testid={`plan-summary-${i}`}>{planSummary(p)}</p>
            <p className="text-[11px] font-mono text-slate-500 mt-2">{p.stores || 0} store{p.stores === 1 ? "" : "s"} on this package</p>
          </div>
        ))}
      </div>
      <PlanForm plan={editing} onClose={() => setEditing(null)} onSaved={saved} />
    </section>
  );
}
